import React, { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/core/api/apiClient'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Collapse,
  CircularProgress,
  Menu,
  MenuItem,
  alpha,
} from '@mui/material'
import {
  Edit,
  Delete,
  PhotoCamera,
  KeyboardArrowUp,
  KeyboardArrowDown,
  Timeline as TimelineIcon,
  Flag as FlagIcon,
  InfoOutlined as InfoOutlinedIcon,
  OpenInNew,
  ChevronLeft,
  ChevronRight,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material'
import dayjs from 'dayjs'
import { Timestamp } from 'firebase/firestore'
import { TransportTruckWithVehicle } from '@/hooks/logistics/useLogistics'
import { getFundedStatusDisplay } from '../../utils/fundedStatusDisplay'
import {
  TransportTruckStatus,
  getTruckStatus,
  getLegStatus,
  ShipmentPlan,
  ShipmentLeg,
  LegFile,
  appendLegStatus,
  getShipmentPlanKind,
  type ShipmentPlanKind,
} from '@otw/models/logistics'
import { ref, uploadBytes, getDownloadURL } from '@firebase/storage'
import { storage } from '@/config/firebase'
import { useAuth } from '@/contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { PickupLocation, RepairShopLocation } from '@otw/models'
import { TransporterGroup } from '@otw/models'
import { useLogistics, useRepairTrucksForTrucks } from 'hooks/logistics/useLogistics'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { usePhotosForInventoryObject } from 'core/inventory/getPhotosForInventoryObject'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { ImageMagnifier } from '@/components/ImageMagnifier'
import ShipmentPlanDialog from '../dialogs/ShipmentPlanDialog'
import LegPhotosDialog from '../dialogs/LegPhotosDialog'
import EditNeedsDialog from '../dialogs/EditNeedsDialog'
import TimelineDialog from '../dialogs/TimelineDialog'
import LegCard from './LegCard'

/** System company ID for shop transport: no transporter assigned, no photos. Leg auto-completes when marked ready. */
export const SHOP_TRANSPORT_COMPANY_ID = 'shop'
export const CUSTOMER_PICKUP_COMPANY_ID = 'customer_pickup'
const SPECIAL_TRANSPORT_COMPANY_IDS = new Set([
  SHOP_TRANSPORT_COMPANY_ID,
  CUSTOMER_PICKUP_COMPANY_ID,
])
const OPTIC_CLIENT_NAME = 'Optic truck works'
const READY_FOR_PICKUP_REPAIR_NOTE = 'Truck marked ready for pickup. Repairs complete.'

const STATUS_CONFIG: Record<
  TransportTruckStatus,
  { label: string; color: 'default' | 'info' | 'primary' | 'error' | 'success' }
> = {
  draft: { label: 'Draft', color: 'default' },
  assigned: { label: 'Awaiting transport', color: 'info' },
  awaiting_transport: { label: 'Awaiting transport', color: 'info' },
  in_transit: { label: 'In transit', color: 'primary' },
  repair: { label: 'Repair', color: 'error' },
  delivered: { label: 'Delivered', color: 'success' },
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

function isRepairShopLocation(
  loc: PickupLocation | RepairShopLocation
): loc is RepairShopLocation {
  return 'shop_type' in loc && Array.isArray(loc.shop_type)
}

function getLocationLabel(loc: PickupLocation | RepairShopLocation): string {
  if (isRepairShopLocation(loc)) {
    return loc.name || loc.contact_name || 'Unknown Shop'
  }
  return loc.contact_name || 'Unknown Contact'
}

function getLocationAddress(loc: PickupLocation | RepairShopLocation): string {
  const parts = [loc.address, loc.city, loc.state, loc.zip].filter(Boolean)
  return parts.join(', ')
}

function getLocationCityState(loc: PickupLocation | RepairShopLocation): string {
  const parts = [loc.city, loc.state].filter(Boolean)
  return parts.join(', ')
}

function getLocationContactInfo(loc: PickupLocation | RepairShopLocation): string {
  const contacts: string[] = []
  if (loc.contact_name) {
    const primaryContact = [loc.contact_name, loc.contact_phone].filter(Boolean).join(' - ')
    if (primaryContact) contacts.push(primaryContact)
  } else if (loc.contact_phone) {
    contacts.push(loc.contact_phone)
  }

  if ('secondary_contact_name' in loc || 'secondary_contact_phone' in loc) {
    const secondaryContact = [loc.secondary_contact_name, loc.secondary_contact_phone]
      .filter(Boolean)
      .join(' - ')
    if (secondaryContact) contacts.push(secondaryContact)
  } else if ('secondary_phone' in loc && loc.secondary_phone) {
    contacts.push(loc.secondary_phone)
  }

  return contacts.join(' | ')
}

const TruckPhotosSection: React.FC<{ vehicleId: string }> = ({ vehicleId }) => {
  const { data: photos, isLoading } = usePhotosForInventoryObject('master_inventory', vehicleId)
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    setCurrentIndex(0)
  }, [vehicleId, photos?.length])

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 160 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (!photos || photos.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 140,
          border: '2px dashed',
          borderColor: 'grey.300',
          borderRadius: 1.5,
          bgcolor: 'grey.100',
        }}
      >
        <PhotoCamera sx={{ fontSize: 36, color: 'grey.400', mb: 0.5 }} />
        <Typography variant="body2" color="text.secondary">
          No photos available for this vehicle
        </Typography>
      </Box>
    )
  }

  const currentPhoto = photos[currentIndex]
  const goPrev = () => {
    setCurrentIndex((prev) => (prev <= 0 ? photos.length - 1 : prev - 1))
  }
  const goNext = () => {
    setCurrentIndex((prev) => (prev >= photos.length - 1 ? 0 : prev + 1))
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {photos.length} photo{photos.length !== 1 ? 's' : ''} available
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {currentIndex + 1} / {photos.length}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton
          size="small"
          onClick={goPrev}
          disabled={photos.length <= 1}
          aria-label="Previous photo"
          sx={{ flexShrink: 0 }}
        >
          <ChevronLeft />
        </IconButton>
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            minWidth: 0,
            aspectRatio: '4 / 3',
            maxHeight: 280,
            borderRadius: 1,
            overflow: 'visible',
            bgcolor: 'grey.200',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ImageMagnifier
            src={currentPhoto.url}
            alt={currentPhoto.name || 'Vehicle photo'}
            sx={{ width: '100%', height: '100%', maxHeight: 280 }}
            imgSx={{ maxHeight: 280 }}
          />
          {currentPhoto.position === 'main' && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                bgcolor: 'primary.main',
                color: 'common.white',
                borderRadius: 0.75,
                px: 0.75,
                py: 0.25,
                fontSize: '0.65rem',
                fontWeight: 700,
              }}
            >
              MAIN
            </Box>
          )}
          <IconButton
            size="small"
            onClick={() => window.open(currentPhoto.url, '_blank')}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              p: 0.5,
              color: 'common.white',
              bgcolor: 'rgba(0,0,0,0.45)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
            }}
            aria-label="Open photo"
          >
            <OpenInNew fontSize="small" />
          </IconButton>
        </Box>
        <IconButton
          size="small"
          onClick={goNext}
          disabled={photos.length <= 1}
          aria-label="Next photo"
          sx={{ flexShrink: 0 }}
        >
          <ChevronRight />
        </IconButton>
      </Box>
      {photos.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 1,
            overflowX: 'auto',
            pb: 0.5,
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
          {photos.map((photo, index) => (
            <Box
              key={photo.url}
              component="button"
              type="button"
              onClick={() => setCurrentIndex(index)}
              aria-label={`View photo ${index + 1}`}
              aria-current={index === currentIndex}
              sx={{
                flexShrink: 0,
                width: 64,
                height: 48,
                p: 0,
                border: 2,
                borderColor: index === currentIndex ? 'primary.main' : 'transparent',
                borderRadius: 1,
                overflow: 'hidden',
                cursor: 'pointer',
                bgcolor: 'grey.200',
                '&:hover': { opacity: 0.9 },
              }}
            >
              <Box
                component="img"
                src={photo.url}
                alt={photo.name || `Photo ${index + 1}`}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            </Box>
          ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}

interface LegWithDetails {
  leg: ShipmentLeg
  fromLabel: string
  toLabel: string
}

interface ShipmentPlanSummaryProps {
  plan: ShipmentPlan
  clientName: string
  createdDateLabel: string
  locationItems: {
    label: string
    address: string
    contactInfo?: string
    isRepair?: boolean
    notes?: string
    locationId?: string
  }[]
  legItems: LegWithDetails[]
  transporterGroups: TransporterGroup[]
  transporterGroupAvgReadyToInProgressMsById: Record<string, number>
  repairShopAvgDropoffToCompleteMsById: Record<string, number>
  transporters: { id: string; first_name?: string; last_name?: string; email?: string }[]
  onEdit: () => void
  onDelete: () => void
  onViewPhotos: (
    planId: string,
    plan: ShipmentPlan,
    legItems: { fromLabel: string; toLabel: string; company_id?: string; to_type?: 'pickup' | 'repair' }[],
    initialLegIndex?: number,
    vehicle_damage?: ShipmentPlan['vehicle_damage'] | null
  ) => void
  onAssignCompany: (legIndex: number, companyId: string) => void
  onSetReadyDate: (legIndex: number, date: string) => void
  onMarkLegReady: (legIndex: number) => void
  onMarkLegNotReady: (legIndex: number) => void
  onMarkLegCompleted: (legIndex: number) => void
  onCancelCharge: (legIndex: number, note: string) => void
  onUploadGatePass: (plan: ShipmentPlan, legIndex: number, files: FileList) => Promise<void>
  gatePassUploading: { planId: string; legIndex: number } | null
  isUpdating: boolean
  isDeleting: boolean
  areLegsExpanded: boolean
  onToggleLegsExpanded: () => void
}

const ShipmentPlanSummary: React.FC<ShipmentPlanSummaryProps> = ({
  plan,
  clientName,
  createdDateLabel,
  locationItems,
  legItems,
  transporterGroups,
  transporterGroupAvgReadyToInProgressMsById,
  repairShopAvgDropoffToCompleteMsById,
  transporters,
  onEdit,
  onDelete,
  onViewPhotos,
  onAssignCompany,
  onSetReadyDate,
  onMarkLegReady,
  onMarkLegNotReady,
  onMarkLegCompleted,
  onCancelCharge,
  onUploadGatePass,
  gatePassUploading,
  isUpdating,
  isDeleting,
  areLegsExpanded,
  onToggleLegsExpanded,
}) => {
  const isMobile = useIsMobile()
  const { data: photosStatus, refetch: refetchPhotosStatus } = useQuery({
    queryKey: ['leg_photos_status', plan.id],
    queryFn: () => apiClient.portal.admin.getLegPhotosStatus(plan.id),
    enabled: !!plan.id && legItems.length > 0 && areLegsExpanded,
    staleTime: 5 * 60 * 1000,
  })
  useEffect(() => {
    if (!areLegsExpanded || !plan.id || legItems.length === 0) return
    void refetchPhotosStatus()
  }, [areLegsExpanded, plan.id, legItems.length, refetchPhotosStatus])
  const statusByLeg = photosStatus?.statusByLeg ?? {}
  const legGridColumns = Math.min(3, Math.max(1, legItems.length))
  const legGridColumnsMd = Math.min(3, legGridColumns)
  const legGridColumnsSm = Math.min(2, legGridColumns)
  const formatAverageDuration = (durationMs?: number) => {
    if (typeof durationMs !== 'number' || !Number.isFinite(durationMs) || durationMs <= 0) return 'N/A'
    const totalMinutes = Math.round(durationMs / (1000 * 60))
    const days = Math.floor(totalMinutes / (60 * 24))
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
    const minutes = totalMinutes % 60
    if (days > 0) return `${days}d ${hours}h`
    if (hours > 0) return `${hours}h ${minutes}m`
    return `${minutes}m`
  }

  return (
    <Box
      sx={{
        mb: 2,
        '&:last-child': { mb: 0 },
        ...(getShipmentPlanKind(plan) === 'post_delivery_repair'
          ? {
              border: '2px solid',
              borderColor: 'warning.main',
              borderRadius: 2,
              p: 1.5,
              bgcolor: (theme) => alpha(theme.palette.warning.main, 0.06),
            }
          : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
            <Typography variant="body2" fontWeight={600}>
              {clientName || OPTIC_CLIENT_NAME}
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 0.75 }}
              >
                ({createdDateLabel})
              </Typography>
            </Typography>
            {getShipmentPlanKind(plan) === 'post_delivery_repair' && (
              <Chip label="Post-delivery repair" size="small" color="warning" />
            )}
          </Box>
          {plan.description && (
            <Typography variant="body2" color="text.secondary">
              {plan.description}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button
            variant="text"
            size="small"
            startIcon={areLegsExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            onClick={onToggleLegsExpanded}
            disabled={legItems.length === 0}
            sx={{ textTransform: 'none' }}
          >
            {areLegsExpanded ? 'Collapse legs' : 'Expand legs'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PhotoCamera />}
            onClick={() =>
                  onViewPhotos(
                    plan.id,
                    plan,
                    legItems.map((l) => {
                      const hist = l.leg?.status_history
                      const latestNotes = hist?.length ? hist[hist.length - 1]?.notes : undefined
                      return {
                        fromLabel: l.fromLabel,
                        toLabel: l.toLabel,
                        company_id: l.leg?.company_id,
                        to_type: l.leg?.to_type,
                        latestStatusNotes: latestNotes,
                      }
                    }),
                    undefined,
                    plan.vehicle_damage ?? undefined
                  )
                }
            sx={{ textTransform: 'none' }}
          >
            View photos
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Edit />}
            onClick={onEdit}
            sx={{ textTransform: 'none' }}
          >
            Edit plan
          </Button>
          <Tooltip title="Delete shipment plan">
            <IconButton
              size="small"
              color="error"
              onClick={onDelete}
              disabled={isDeleting}
              sx={{ p: 0.5 }}
              aria-label="Delete plan"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
        {/* Row 1: Location timeline */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 1,
            alignItems: 'stretch',
            width: '100%',
          }}
        >
          {locationItems.map((item, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && (
                <Box
                  sx={{
                    flexShrink: 0,
                    alignSelf: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {isMobile ? '↓' : '→'}
                  </Typography>
                </Box>
              )}
              <Card
                variant="outlined"
                sx={{
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={600} noWrap title={item.label}>
                      {item.label}
                    </Typography>
                    {item.isRepair && (
                      <Chip
                        label="Repair"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          bgcolor: 'error.100',
                          color: 'error.dark',
                        }}
                      />
                    )}
                    {item.isRepair && (
                      <Chip
                        size="small"
                        color="secondary"
                        label={`Repair time: ${formatAverageDuration(
                          item.locationId ? repairShopAvgDropoffToCompleteMsById[item.locationId] : undefined
                        )}`}
                        sx={{ height: 22, '& .MuiChip-label': { px: 1 } }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" noWrap title={item.address}>
                    {item.address || '—'}
                  </Typography>
                  {item.contactInfo && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 0.25 }}
                      title={item.contactInfo}
                    >
                      Contact: {item.contactInfo}
                    </Typography>
                  )}
                  {item.notes && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }} title={item.notes}>
                      {item.notes}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </React.Fragment>
          ))}
          {locationItems.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', py: 2 }}>
              No locations
            </Typography>
          )}
        </Box>

        {/* Row 2: Leg cards - full width, equal height */}
        {areLegsExpanded && legItems.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isMobile
                ? '1fr'
                : {
                    xs: '1fr',
                    sm: `repeat(${legGridColumnsSm}, minmax(0, 1fr))`,
                    md: `repeat(${legGridColumnsMd}, minmax(0, 1fr))`,
                    lg: `repeat(${legGridColumns}, minmax(0, 1fr))`,
                  },
              width: '100%',
              gap: 1,
            }}
          >
            {legItems.map((legItem, idx) => (
              <LegCard
                key={idx}
                plan={plan}
                legItem={legItem}
                legIndex={idx}
                hasPhotos={
                  !!(statusByLeg[idx]?.hasPreDelivery || statusByLeg[idx]?.hasPostDelivery)
                }
                onOpenPhotos={() =>
                  onViewPhotos(
                    plan.id,
                    plan,
                    legItems.map((l) => {
                      const hist = l.leg?.status_history
                      const latestNotes = hist?.length ? hist[hist.length - 1]?.notes : undefined
                      return {
                        fromLabel: l.fromLabel,
                        toLabel: l.toLabel,
                        company_id: l.leg?.company_id,
                        to_type: l.leg?.to_type,
                        latestStatusNotes: latestNotes,
                      }
                    }),
                    idx,
                    plan.vehicle_damage ?? undefined
                  )
                }
                transporterGroups={transporterGroups}
                transporterGroupAvgReadyToInProgressMsById={transporterGroupAvgReadyToInProgressMsById}
                transporters={transporters}
                isUpdating={isUpdating}
                onAssignCompany={onAssignCompany}
                onSetReadyDate={onSetReadyDate}
                onMarkLegReady={onMarkLegReady}
                onMarkLegNotReady={onMarkLegNotReady}
                onMarkLegCompleted={onMarkLegCompleted}
                onCancelCharge={onCancelCharge}
                onUploadGatePass={onUploadGatePass}
                isUploadingGatePass={
                  gatePassUploading?.planId === plan.id && gatePassUploading?.legIndex === idx
                }
                shopTransportCompanyId={SHOP_TRANSPORT_COMPANY_ID}
                customerPickupCompanyId={CUSTOMER_PICKUP_COMPANY_ID}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  )
}

interface TransportTruckCardProps {
  truckWithVehicle: TransportTruckWithVehicle
  repairTrucksMap: Map<string, { id: string; flagged?: boolean; work_notes?: Array<{ note?: string }> }>
  clientUsersById: Map<string, { id: string; first_name?: string; last_name?: string }>
  pickupLocationsById: Map<string, PickupLocation>
  repairShopLocationsById: Map<string, RepairShopLocation>
  transporterGroups: TransporterGroup[]
  transporters: { id: string; first_name?: string; last_name?: string; email?: string }[]
}

function resolveLocationLabel(
  locId: string,
  locType: string,
  pickupLocationsById: Map<string, PickupLocation>,
  repairShopLocationsById: Map<string, RepairShopLocation>
): string {
  const found =
    locType === 'repair'
      ? repairShopLocationsById.get(locId)
      : pickupLocationsById.get(locId)
  return found ? getLocationLabel(found) : 'Unknown'
}

const TransportTruckCard: React.FC<TransportTruckCardProps> = ({
  truckWithVehicle,
  repairTrucksMap,
  clientUsersById,
  pickupLocationsById,
  repairShopLocationsById,
  transporterGroups,
  transporters,
}) => {
  const [isShipmentPlanDialogOpen, setIsShipmentPlanDialogOpen] = useState(false)
  const [planToEdit, setPlanToEdit] = useState<ShipmentPlan | null>(null)
  const [createPlanKind, setCreatePlanKind] = useState<ShipmentPlanKind>('standard')
  const [insertRepairAfterLegIndex, setInsertRepairAfterLegIndex] = useState<number | null>(null)
  const [legPhotosState, setLegPhotosState] = useState<{
    planId: string
    plan: ShipmentPlan
    legItems: { fromLabel: string; toLabel: string; company_id?: string; to_type?: 'pickup' | 'repair' }[]
    initialLegIndex?: number
    vehicle_damage?: ShipmentPlan['vehicle_damage'] | null
  } | null>(null)
  const [isEditNeedsDialogOpen, setIsEditNeedsDialogOpen] = useState(false)
  const [completeConfirm, setCompleteConfirm] = useState<{
    plan: ShipmentPlan
    legIndex: number
  } | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<
    { type: 'plan'; plan: ShipmentPlan } | { type: 'truck' } | null
  >(null)
  const [newPlanConfirmOpen, setNewPlanConfirmOpen] = useState(false)
  const [pendingCreatePlanKind, setPendingCreatePlanKind] =
    useState<ShipmentPlanKind>('standard')
  const [newPlanMenuAnchor, setNewPlanMenuAnchor] = useState<null | HTMLElement>(null)
  const [flagConfirm, setFlagConfirm] = useState<{
    repairTruckId: string
    nextFlagged: boolean
  } | null>(null)
  const [gatePassUploading, setGatePassUploading] = useState<{
    planId: string
    legIndex: number
  } | null>(null)
  const [isTimelineDialogOpen, setIsTimelineDialogOpen] = useState(false)
  const [isMoreInfoOpen, setIsMoreInfoOpen] = useState(false)
  const { truck, vehicle, shipmentPlans } = truckWithVehicle
  const expandedPlansStorageKey = useMemo(
    () => `transport-truck-expanded-plans:${truck.id}`,
    [truck.id]
  )
  const [expandedPlanIds, setExpandedPlanIds] = useState<string[]>([])

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const raw = window.localStorage.getItem(expandedPlansStorageKey)
      if (!raw) {
        setExpandedPlanIds([])
        return
      }
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        setExpandedPlanIds(parsed.filter((id): id is string => typeof id === 'string'))
      } else {
        setExpandedPlanIds([])
      }
    } catch {
      setExpandedPlanIds([])
    }
  }, [expandedPlansStorageKey])

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(expandedPlansStorageKey, JSON.stringify(expandedPlanIds))
  }, [expandedPlanIds, expandedPlansStorageKey])
  const { userInfo } = useAuth()
  const { showSuccess, showError } = useSnackbar()
  const {
    updateShipmentPlan,
    isUpdatingShipmentPlan,
    updateTransportTruck,
    revertLatestTransportTruckStatus,
    approveLegPhotos,
    deleteShipmentPlan,
    isDeletingShipmentPlan,
    deleteTransportTruck,
    isDeletingTransportTruck,
    addRepairTruckNote,
    deleteRepairTruckNote,
    updateRepairTruckFlagged,
    isUpdatingRepairTruckFlagged,
    transporterGroupAvgReadyToInProgressMsById,
    repairShopAvgDropoffToCompleteMsById,
  } = useLogistics()
  const singleTruckRepairTrucksMap = useRepairTrucksForTrucks([truck.id])

  const yearMakeModel = vehicle
    ? `${vehicle.year ?? ''} ${vehicle.make ?? ''} ${vehicle.model ?? ''}`.trim()
    : '—'
  const truckNotes = vehicle?.truck_notes?.trim() || ''
  const vinLast4 = truck.vin?.slice(-4) ?? '—'
  const truckStatus = getTruckStatus(truck)
  const statusConfig = STATUS_CONFIG[truckStatus] ?? STATUS_CONFIG.draft
  const fundedStatusDisplay = getFundedStatusDisplay(vehicle)
  const currentRepairTruck = truck.currentLocationId
    ? repairTrucksMap.get(`${truck.id}_${truck.currentLocationId}`) ??
      singleTruckRepairTrucksMap.get(`${truck.id}_${truck.currentLocationId}`)
    : null
  const isCurrentRepairFlagged = Boolean(currentRepairTruck?.flagged)
  const formatDateLabel = (value?: string | null): string => {
    if (!value) return '—'
    const parsed = dayjs(value)
    return parsed.isValid() ? parsed.format('MMM D, YYYY') : '—'
  }
  const formatBooleanWithDate = (
    value: boolean | null | undefined,
    date?: string | null
  ): string => {
    if (value === true) {
      const dateLabel = formatDateLabel(date)
      return date ? `Yes (${dateLabel})` : 'Yes'
    }
    if (value === false) return 'No'
    return '—'
  }
  const formatDaysSince = (date?: string | null): string => {
    if (!date) return '—'
    const parsed = dayjs(date)
    if (!parsed.isValid()) return '—'
    const days = Math.max(0, dayjs().diff(parsed, 'day'))
    return `${days.toLocaleString('en-US')} day${days !== 1 ? 's' : ''}`
  }
  const sellingOrSoldTo =
    vehicle?.selling_to_user?.name?.trim() ||
    vehicle?.sold_to_user?.name?.trim() ||
    vehicle?.who?.trim() ||
    '—'
  const inventoryStatus = vehicle?.status || '—'
  const stockNumber = vehicle?.stock_number?.trim() || '—'
  const boughtFrom = vehicle?.bought_from?.trim() || '—'
  const mechanicalStatus = vehicle?.mechanical_status || '—'
  const bodyStatus = vehicle?.body_status || '—'
  const paidFor = formatBooleanWithDate(vehicle?.paid_for, vehicle?.paid_for_date)
  const funded = formatBooleanWithDate(vehicle?.funded, vehicle?.funded_date)
  const daysSincePaid = formatDaysSince(
    vehicle?.paid_for_date ?? vehicle?.title_tracking?.paid_date
  )
  const daysSinceFunded = formatDaysSince(vehicle?.funded_date)
  const estimatedRepairCost =
    typeof vehicle?.estimated_repair_cost === 'number'
      ? currencyFormatter.format(vehicle.estimated_repair_cost)
      : '—'
  const truckSize = vehicle?.size?.trim() || '—'
  const truckDetails = [
    { label: 'Bought from', value: boughtFrom },
    { label: 'Days since funded', value: daysSinceFunded },
    { label: 'Days since paid', value: daysSincePaid },
    { label: 'Selling/Sold to', value: sellingOrSoldTo },
    { label: 'Inventory status', value: inventoryStatus },
    { label: 'Stock number', value: stockNumber },
    { label: 'Tier', value: vehicle?.tier || '—' },
    { label: 'Size', value: truckSize },
    { label: 'Paid for', value: paidFor },
    { label: 'Funded', value: funded },
    { label: 'Mechanical status', value: mechanicalStatus },
    { label: 'Body status', value: bodyStatus },
    { label: 'Estimated repair cost', value: estimatedRepairCost },
  ]

  const resolveCityStateByIdType = (
    locId: string,
    locType: 'pickup' | 'repair'
  ): string => {
    const found =
      locType === 'repair'
        ? repairShopLocationsById.get(locId)
        : pickupLocationsById.get(locId)
    if (!found) return 'Unknown'
    return getLocationCityState(found) || getLocationLabel(found)
  }

  const currentLocationLabel = useMemo(() => {
    if (truckStatus === 'in_transit') {
      for (const plan of shipmentPlans) {
        const inProgressLeg = (plan.shipment_legs ?? []).find(
          (leg) => getLegStatus(leg) === 'In progress'
        )
        if (inProgressLeg) {
          const fromLabel = resolveCityStateByIdType(inProgressLeg.from_id, inProgressLeg.from_type)
          const toLabel = resolveCityStateByIdType(inProgressLeg.to_id, inProgressLeg.to_type)
          return `${fromLabel} → ${toLabel}`
        }
      }
    }

    const currentLocationId = truck.currentLocationId
    if (!currentLocationId) return ''

    const currentRepairLocation = repairShopLocationsById.get(currentLocationId)
    if (currentRepairLocation) {
      return getLocationLabel(currentRepairLocation)
    }

    const currentPickupLocation = pickupLocationsById.get(currentLocationId)
    if (currentPickupLocation) {
      return getLocationCityState(currentPickupLocation) || getLocationLabel(currentPickupLocation)
    }

    return ''
  }, [truckStatus, shipmentPlans, truck.currentLocationId, repairShopLocationsById, pickupLocationsById])

  const isAwaitingTransportLeg = (candidateLeg?: ShipmentLeg): boolean => {
    if (!candidateLeg) return false
    const candidateStatus = getLegStatus(candidateLeg)
    return (
      candidateLeg.ready === true &&
      !!candidateLeg.company_id &&
      (candidateStatus === 'Draft' ||
        candidateStatus === 'Pending driver' ||
        candidateStatus === 'Assigned')
    )
  }

  const plansWithDetails = useMemo(() => {
    return shipmentPlans.map((plan) => {
      const isOpticPlan = !plan.client_user_id || plan.client_user_id === 'optic'
      const client = plan.client_user_id
        ? clientUsersById.get(plan.client_user_id)
        : null
      const clientName = isOpticPlan
        ? OPTIC_CLIENT_NAME
        : client
          ? `${client.first_name} ${client.last_name}`.trim()
          : ''
      const createdDate = plan.created_at?.toDate?.()
      const createdDateLabel = createdDate ? dayjs(createdDate).format('MMM D, YYYY') : 'Unknown date'
      const locationItems = (plan.locations ?? []).map((loc) => {
        const found =
          loc.location_type === 'repair'
            ? repairShopLocationsById.get(loc.location_id)
            : pickupLocationsById.get(loc.location_id)
        return {
          locationId: loc.location_id,
          label: found ? getLocationLabel(found) : 'Unknown',
          address: found ? getLocationAddress(found) : '',
          contactInfo: found ? getLocationContactInfo(found) : '',
          isRepair: loc.location_type === 'repair',
          notes: loc.notes ?? '',
        }
      })
      const legItems: LegWithDetails[] = (plan.shipment_legs ?? []).map((leg) => ({
        leg,
        fromLabel: resolveLocationLabel(
          leg.from_id,
          leg.from_type,
          pickupLocationsById,
          repairShopLocationsById
        ),
        toLabel: resolveLocationLabel(
          leg.to_id,
          leg.to_type,
          pickupLocationsById,
          repairShopLocationsById
        ),
      }))
      return {
        plan,
        clientName: clientName || OPTIC_CLIENT_NAME,
        createdDateLabel,
        locationItems,
        legItems,
      }
    })
  }, [shipmentPlans, clientUsersById, pickupLocationsById, repairShopLocationsById])

  const handleAssignCompany = (plan: ShipmentPlan, legIndex: number, companyId: string) => {
    const legs = [...(plan.shipment_legs ?? [])]
    if (!legs[legIndex]) return
    const currentCompanyId = legs[legIndex].company_id ?? ''
    if (currentCompanyId === companyId) return
    const existingCompanyHistory = legs[legIndex].company_history ?? []
    const nextCompanyHistory = companyId
      ? [
          ...existingCompanyHistory,
          {
            company_id: companyId,
            timestamp: Timestamp.now(),
          },
        ]
      : existingCompanyHistory
    const isRealTransportCompany = Boolean(companyId) && !SPECIAL_TRANSPORT_COMPANY_IDS.has(companyId)
    const isSpecialTransportCompany = Boolean(companyId) && SPECIAL_TRANSPORT_COMPANY_IDS.has(companyId)
    const nextStatus = isRealTransportCompany
      ? 'Pending driver'
      : isSpecialTransportCompany
        ? 'Pending completion'
        : 'Draft'
    const nextStatusNote = isRealTransportCompany
      ? 'Transport company assigned and awaiting driver'
      : isSpecialTransportCompany
        ? 'Special transport selected and awaiting completion'
        : 'Company changed'
    const updatedLeg = appendLegStatus(
      {
        ...legs[legIndex],
        company_id: companyId || undefined,
        company_history: nextCompanyHistory,
        rejected: false,
        // Changing transport company invalidates prior driver/assignment details.
        // updateShipmentPlan maps undefined -> null in Firestore.
        driver_id: undefined,
        assignment_notes: undefined,
        assignment_notes_photo: undefined,
        dispatch_info: undefined,
        estimated_shipping_price: undefined,
        scheduled_pickup_date: undefined,
      },
      nextStatus,
      Timestamp.now(),
      nextStatusNote
    )
    legs[legIndex] = {
      ...updatedLeg,
    }
    updateShipmentPlan({
      planId: plan.id,
      client_user_id: plan.client_user_id ?? null,
      description: plan.description ?? null,
      locations: plan.locations ?? [],
      shipment_legs: legs,
    })
  }

  const handleSetReadyDate = async (plan: ShipmentPlan, legIndex: number, date: string) => {
    const legs = [...(plan.shipment_legs ?? [])]
    if (!legs[legIndex]) return
    let leg = legs[legIndex]
    const wasReady = leg.ready === true
    const today = dayjs().format('YYYY-MM-DD')
    const isDateTodayOrPast = date <= today
    leg = {
      ...leg,
      ready_date: date,
      ready: isDateTodayOrPast,
    }

    // Special transport (shop/customer pickup): auto-complete leg when marked ready (no photos)
    if (isDateTodayOrPast && leg.company_id && SPECIAL_TRANSPORT_COMPANY_IDS.has(leg.company_id)) {
      const completionNote =
        leg.company_id === SHOP_TRANSPORT_COMPANY_ID ? 'Shop transport' : 'Customer pickup'
      leg = appendLegStatus(leg, 'Completed', Timestamp.now(), completionNote)
      // Move truck location to leg destination; set status:
      // - repair if going to repair location
      // - delivered if this is the last leg and destination is not repair
      // - otherwise keep current status
      const isLastLeg = legIndex === legs.length - 1
      const newStatus: TransportTruckStatus =
        leg.to_type === 'repair'
          ? 'repair'
          : isLastLeg
            ? 'delivered'
            : getTruckStatus(truck)
      updateTransportTruck({
        truckId: truck.id,
        status: newStatus,
        currentLocationId: leg.to_id,
      })
    }
    legs[legIndex] = leg

    // Only the next actionable leg can drive "awaiting transport" status.
    const nextActiveLeg = legs.find((candidateLeg) => getLegStatus(candidateLeg) !== 'Completed')
    if (isAwaitingTransportLeg(nextActiveLeg)) {
      updateTransportTruck({
        truckId: truck.id,
        status: 'awaiting_transport',
        // Keep truck location aligned with the leg that is now awaiting pickup.
        // This is especially important for first-leg readiness where there may be
        // no prior currentLocationId on the transport truck.
        currentLocationId: nextActiveLeg?.from_id ?? truck.currentLocationId ?? null,
      })
    }

    await updateShipmentPlan({
      planId: plan.id,
      client_user_id: plan.client_user_id ?? null,
      description: plan.description ?? null,
      locations: plan.locations ?? [],
      shipment_legs: legs,
    })

    // If truck is currently at a repair shop and the next active leg was just marked ready,
    // add a repair timeline note indicating the truck is ready for pickup.
    const nextActiveLegIndex = legs.findIndex((l) => getLegStatus(l) !== 'Completed')
    const wasJustMarkedReady = !wasReady && leg.ready === true
    const currentRepairTruck = truck.currentLocationId
      ? repairTrucksMap.get(`${truck.id}_${truck.currentLocationId}`) ??
        singleTruckRepairTrucksMap.get(`${truck.id}_${truck.currentLocationId}`)
      : null
    const isTruckAtRepairShop = Boolean(currentRepairTruck)

    if (
      wasJustMarkedReady &&
      legIndex === nextActiveLegIndex &&
      isTruckAtRepairShop &&
      currentRepairTruck
    ) {
      try {
        await addRepairTruckNote({
          repairTruckId: currentRepairTruck.id,
          note: READY_FOR_PICKUP_REPAIR_NOTE,
        })
      } catch {
        // Non-blocking: leg update already succeeded.
      }
    }
  }

  const handleMarkLegCompleted = (plan: ShipmentPlan, legIndex: number) => {
    const legs = [...(plan.shipment_legs ?? [])]
    if (!legs[legIndex]) return
    const today = dayjs().format('YYYY-MM-DD')
    let leg = legs[legIndex]
    leg = {
      ...leg,
      ready_date: leg.ready_date ?? today,
      ready: true,
    }
    leg = appendLegStatus(leg, 'Completed', Timestamp.now(), 'Marked completed by admin')
    const isLastLeg = legIndex === legs.length - 1
    const newStatus: TransportTruckStatus =
      leg.to_type === 'repair'
        ? 'repair'
        : isLastLeg
          ? 'delivered'
          : getTruckStatus(truck)
    updateTransportTruck({
      truckId: truck.id,
      status: newStatus,
      currentLocationId: leg.to_id,
    })
    legs[legIndex] = leg
    updateShipmentPlan({
      planId: plan.id,
      client_user_id: plan.client_user_id ?? null,
      description: plan.description ?? null,
      locations: plan.locations ?? [],
      shipment_legs: legs,
    })
  }

  const handleConfirmMarkLegCompleted = async () => {
    if (!completeConfirm) return
    handleMarkLegCompleted(completeConfirm.plan, completeConfirm.legIndex)
    setCompleteConfirm(null)
  }

  const handleCancelCharge = (plan: ShipmentPlan, legIndex: number, note: string) => {
    const legs = [...(plan.shipment_legs ?? [])]
    const leg = legs[legIndex]
    if (!leg) return
    legs[legIndex] = {
      ...leg,
      charge_cancelled: true,
      charge_cancelled_note: note,
      charge_cancelled_at: Timestamp.now(),
      charge_cancelled_by: userInfo?.id ?? 'unknown',
    }
    updateShipmentPlan({
      planId: plan.id,
      client_user_id: plan.client_user_id ?? null,
      description: plan.description ?? null,
      locations: plan.locations ?? [],
      shipment_legs: legs,
    })
  }

  const handleMarkLegReady = async (plan: ShipmentPlan, legIndex: number) => {
    await handleSetReadyDate(plan, legIndex, dayjs().format('YYYY-MM-DD'))
  }

  const handleMarkLegNotReady = async (plan: ShipmentPlan, legIndex: number) => {
    const legs = [...(plan.shipment_legs ?? [])]
    const leg = legs[legIndex]
    if (!leg) return
    const currentStatus = getLegStatus(leg)
    if (
      currentStatus !== 'Draft' &&
      currentStatus !== 'Pending driver' &&
      currentStatus !== 'Assigned'
    ) {
      return
    }
    const updatedLeg: ShipmentLeg = {
      ...leg,
      ready: false,
      ready_date: undefined,
    }
    legs[legIndex] = updatedLeg
    await updateShipmentPlan({
      planId: plan.id,
      client_user_id: plan.client_user_id ?? null,
      description: plan.description ?? null,
      locations: plan.locations ?? [],
      shipment_legs: legs,
    })
    await revertLatestTransportTruckStatus({ truckId: truck.id })

    const repairTruckForRollback = truck.currentLocationId
      ? repairTrucksMap.get(`${truck.id}_${truck.currentLocationId}`) ??
        singleTruckRepairTrucksMap.get(`${truck.id}_${truck.currentLocationId}`)
      : null
    if (!repairTruckForRollback) return

    const workNotes = repairTruckForRollback.work_notes ?? []
    const readyNoteIndexFromEnd = [...workNotes]
      .reverse()
      .findIndex(
        (entry) =>
          (entry.note ?? '').trim().toLowerCase() ===
          READY_FOR_PICKUP_REPAIR_NOTE.toLowerCase()
      )
    if (readyNoteIndexFromEnd < 0) return

    const readyNoteIndex = workNotes.length - 1 - readyNoteIndexFromEnd
    await deleteRepairTruckNote({
      repairTruckId: repairTruckForRollback.id,
      noteIndex: readyNoteIndex,
    })
  }

  const handleOpenCreate = (kind: ShipmentPlanKind = 'standard') => {
    setCreatePlanKind(kind)
    setPlanToEdit(null)
    setIsShipmentPlanDialogOpen(true)
  }
  const handleRequestCreate = (kind: ShipmentPlanKind = 'standard') => {
    setPendingCreatePlanKind(kind)
    if (shipmentPlans.length > 0) {
      setNewPlanConfirmOpen(true)
      return
    }
    handleOpenCreate(kind)
  }

  const handleOpenEdit = (plan: ShipmentPlan) => {
    setCreatePlanKind(getShipmentPlanKind(plan))
    setPlanToEdit(plan)
    setInsertRepairAfterLegIndex(null)
    setIsShipmentPlanDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsShipmentPlanDialogOpen(false)
    setPlanToEdit(null)
    setInsertRepairAfterLegIndex(null)
    setCreatePlanKind('standard')
  }

  const handleRejectPreDelivery = (planId: string, legIndex: number) => {
    const plan = plansWithDetails.find((p) => p.plan.id === planId)?.plan
    if (!plan) return
    setLegPhotosState(null)
    setPlanToEdit(plan)
    setInsertRepairAfterLegIndex(legIndex)
    setIsShipmentPlanDialogOpen(true)
  }

  const handleApprovePreDelivery = async (planId: string, legIndex: number) => {
    const plan = plansWithDetails.find((p) => p.plan.id === planId)?.plan
    if (!plan) return
    await approveLegPhotos({ plan, legIndex })
  }

  const handleDeletePlan = (plan: ShipmentPlan) => {
    setDeleteConfirm({ type: 'plan', plan })
  }

  const handleDeleteTruck = () => {
    setDeleteConfirm({ type: 'truck' })
  }

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return
    try {
      if (deleteConfirm.type === 'plan') {
        await deleteShipmentPlan(deleteConfirm.plan.id)
      } else {
        await deleteTransportTruck(truck.id)
      }
      setDeleteConfirm(null)
    } catch {
      // Error shown by mutation
    }
  }

  const handleUploadGatePass = async (
    plan: ShipmentPlan,
    legIndex: number,
    files: FileList
  ) => {
    if (!files?.length || !storage) return
    setGatePassUploading({ planId: plan.id, legIndex })
    const uploaded: LegFile[] = []
    const userId = userInfo?.id ?? 'unknown'
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const ext = file.name.split('.').pop() || ''
        const base = file.name.substring(0, file.name.lastIndexOf('.')) || file.name
        const sanitized = base.replace(/[^a-zA-Z0-9._-]/g, '_').replace(/_{2,}/g, '_')
        const fileName = `${Date.now()}_${sanitized}${ext ? '.' + ext : ''}`
                const storagePath = `shipment_plans/${plan.id}/legs/${legIndex}/gate_passes/${fileName}`
        const storageRef = ref(storage, storagePath)
        const uploadResult = await uploadBytes(storageRef, file)
        const url = await getDownloadURL(uploadResult.ref)
        uploaded.push({
          fileName: file.name,
          storagePath,
          url,
          fileSize: file.size,
          fileType: file.type,
          uploadedAt: Timestamp.now(),
          uploadedBy: userId,
        })
      }
      if (uploaded.length === 0) return
      const legs = [...(plan.shipment_legs ?? [])]
      const leg = legs[legIndex]
      if (!leg) return
      legs[legIndex] = {
        ...leg,
        gate_passes: [...(leg.gate_passes ?? []), ...uploaded],
      }
      await updateShipmentPlan({
        planId: plan.id,
        client_user_id: plan.client_user_id ?? null,
        description: plan.description ?? null,
        locations: plan.locations ?? [],
        shipment_legs: legs,
      })
      showSuccess(`Uploaded ${uploaded.length} gate pass(es)`)
    } catch (e) {
      showError(`Failed to upload: ${e instanceof Error ? e.message : 'Unknown error'}`)
    } finally {
      setGatePassUploading(null)
    }
  }

  const deleteConfirmTitle =
    deleteConfirm?.type === 'plan'
      ? 'Delete shipment plan?'
      : deleteConfirm?.type === 'truck'
        ? 'Delete transport truck?'
        : ''
  const deleteConfirmMessage =
    deleteConfirm?.type === 'plan'
      ? (() => {
          const plan = deleteConfirm.plan
          const clientName =
            plan.client_user_id
              ? clientUsersById.get(plan.client_user_id)
              : null
          const label = clientName
            ? `${clientName.first_name} ${clientName.last_name}`.trim()
            : plan.description || plan.id
          return `Delete this shipment plan (${label})? This cannot be undone.`
        })()
      : deleteConfirm?.type === 'truck'
        ? `Delete this transport truck (${yearMakeModel && vinLast4 ? `${yearMakeModel} (${vinLast4})` : truck.vin || truck.id})? All ${shipmentPlans.length} shipment plan(s) will also be deleted. This cannot be undone.`
        : ''

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderWidth: isCurrentRepairFlagged ? 2 : 1,
        borderColor: isCurrentRepairFlagged ? 'warning.main' : undefined,
        '&:hover': {
          boxShadow: 1,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {yearMakeModel || 'Unknown vehicle'} {vinLast4 ? `(${vinLast4})` : ''}
              </Typography>
              <Tooltip
                title={
                  truckNotes ? (
                    <Box sx={{ whiteSpace: 'pre-wrap', maxWidth: 360 }}>
                      {truckNotes}
                    </Box>
                  ) : 'No truck notes'
                }
                arrow
              >
                <span>
                  <IconButton size="small" sx={{ p: 0.25 }} aria-label="View truck notes">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Chip
              label={statusConfig.label}
              color={statusConfig.color}
              size="small"
              sx={{
                ...(statusConfig.color === 'default' && {
                  bgcolor: 'grey.200',
                  color: 'grey.700',
                }),
              }}
            />
            <Chip
              label={fundedStatusDisplay.label}
              color={fundedStatusDisplay.chipColor}
              size="small"
              variant={
                fundedStatusDisplay.chipColor === 'error' ||
                fundedStatusDisplay.chipColor === 'default'
                  ? 'outlined'
                  : 'filled'
              }
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {!!currentLocationLabel && (
                <Typography variant="body2" color="text.primary" fontWeight={500}>
                  {currentLocationLabel}
                </Typography>
              )}
              <Tooltip
                title={
                  currentRepairTruck
                    ? isCurrentRepairFlagged
                      ? 'Remove flag'
                      : 'Flag truck'
                    : 'No current repair location to flag'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (!currentRepairTruck) return
                      setFlagConfirm({
                        repairTruckId: currentRepairTruck.id,
                        nextFlagged: !isCurrentRepairFlagged,
                      })
                    }}
                    sx={{ p: 0.25 }}
                    aria-label={isCurrentRepairFlagged ? 'Remove flag' : 'Flag truck'}
                    disabled={!currentRepairTruck || isUpdatingRepairTruckFlagged}
                  >
                    <FlagIcon
                      fontSize="small"
                      sx={{ color: isCurrentRepairFlagged ? 'warning.main' : 'action.disabled' }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="View timeline">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setIsTimelineDialogOpen(true)}
                    sx={{ p: 0.5 }}
                    aria-label="View timeline"
                    disabled={!vehicle}
                  >
                    <TimelineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setIsMoreInfoOpen((prev) => !prev)}
                startIcon={isMoreInfoOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                sx={{ textTransform: 'none', minWidth: 0, px: 1 }}
              >
                More info
              </Button>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!truck.installation_status?.anti_theft &&
            !truck.installation_status?.cameras &&
            !truck.installation_status?.backup_sensors ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Edit />}
                onClick={() => setIsEditNeedsDialogOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Add installation requirements
              </Button>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  flexWrap: 'wrap',
                  p: 0.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                {truck.installation_status &&
                  (
                    [
                      ['anti_theft', 'Anti-theft'] as const,
                      ['cameras', 'Cameras'] as const,
                      ['backup_sensors', 'Backup sensors'] as const,
                    ] as const
                  ).map(([key, label]) => {
                    const status = truck.installation_status?.[key]
                    if (!status) return null
                    const chipColor: 'error' | 'warning' | 'success' =
                      status === 'installed' ? 'success' : status === 'accepted' ? 'warning' : 'error'
                    return (
                      <Chip
                        key={key}
                        label={label}
                        size="small"
                        color={chipColor}
                        sx={{ fontSize: '0.7rem' }}
                      />
                    )
                  })}
                <Tooltip title="Edit vehicle needs">
                  <IconButton
                    size="small"
                    onClick={() => setIsEditNeedsDialogOpen(true)}
                    sx={{ color: 'primary.main', p: 0.5 }}
                    aria-label="Edit vehicle needs"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {truckStatus === 'delivered' ? (
              <>
                <Button
                  variant="contained"
                  size="small"
                  endIcon={<ArrowDropDownIcon />}
                  onClick={(event) => setNewPlanMenuAnchor(event.currentTarget)}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  New shipment plan
                </Button>
                <Menu
                  anchorEl={newPlanMenuAnchor}
                  open={Boolean(newPlanMenuAnchor)}
                  onClose={() => setNewPlanMenuAnchor(null)}
                >
                  <MenuItem
                    onClick={() => {
                      setNewPlanMenuAnchor(null)
                      handleRequestCreate('standard')
                    }}
                  >
                    Standard shipment plan
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setNewPlanMenuAnchor(null)
                      handleRequestCreate('post_delivery_repair')
                    }}
                  >
                    Post-delivery repair
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                variant="contained"
                size="small"
                onClick={() => handleRequestCreate('standard')}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                New shipment plan
              </Button>
            )}
            <Tooltip title="Delete transport truck">
              <IconButton
                size="small"
                color="error"
                onClick={handleDeleteTruck}
                disabled={isDeletingTransportTruck}
                aria-label="Delete truck"
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Collapse in={isMoreInfoOpen} timeout="auto" unmountOnExit>
          <Box
            sx={{
              mt: 1.5,
              mb: 2,
              p: 1.5,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            <Box
              sx={{
                mb: 1,
                p: 1,
                borderRadius: 1,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                VIN
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  letterSpacing: 0.5,
                }}
              >
                {truck.vin?.trim() || '—'}
              </Typography>
            </Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Truck information
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 1,
              }}
            >
              {truckDetails.map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                mt: 1,
                p: 1,
                borderRadius: 1,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Truck notes
              </Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >
                {truckNotes || '—'}
              </Typography>
            </Box>
            {vehicle?.id && (
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Truck photos
                </Typography>
                <TruckPhotosSection vehicleId={vehicle.id} />
              </Box>
            )}
          </Box>
        </Collapse>

        {shipmentPlans.length > 0 && (
          <Box sx={{ width: '100%', borderTop: 1, borderColor: 'divider', pt: 2 }}>
            {plansWithDetails.map(({ plan, clientName, createdDateLabel, locationItems, legItems }) => (
              <ShipmentPlanSummary
                key={plan.id}
                plan={plan}
                clientName={clientName}
                createdDateLabel={createdDateLabel}
                locationItems={locationItems}
                legItems={legItems}
                transporterGroups={transporterGroups}
                transporterGroupAvgReadyToInProgressMsById={transporterGroupAvgReadyToInProgressMsById}
                repairShopAvgDropoffToCompleteMsById={repairShopAvgDropoffToCompleteMsById}
                transporters={transporters}
                onEdit={() => handleOpenEdit(plan)}
                onDelete={() => handleDeletePlan(plan)}
                onViewPhotos={(planId, plan, legItemsForPhotos, initialLegIndex, vehicle_damage) =>
                  setLegPhotosState({
                    planId,
                    plan,
                    legItems: legItemsForPhotos,
                    initialLegIndex,
                    vehicle_damage: vehicle_damage ?? null,
                  })
                }
                onAssignCompany={(legIndex, companyId) =>
                  handleAssignCompany(plan, legIndex, companyId)
                }
                onSetReadyDate={(legIndex, date) => handleSetReadyDate(plan, legIndex, date)}
                onMarkLegReady={(legIndex) => handleMarkLegReady(plan, legIndex)}
                onMarkLegNotReady={(legIndex) => handleMarkLegNotReady(plan, legIndex)}
                onMarkLegCompleted={(legIndex) =>
                  setCompleteConfirm({
                    plan,
                    legIndex,
                  })
                }
                onCancelCharge={(legIndex, note) => handleCancelCharge(plan, legIndex, note)}
                onUploadGatePass={handleUploadGatePass}
                gatePassUploading={gatePassUploading}
                isUpdating={isUpdatingShipmentPlan}
                isDeleting={isDeletingShipmentPlan}
                areLegsExpanded={expandedPlanIds.includes(plan.id)}
                onToggleLegsExpanded={() =>
                  setExpandedPlanIds((prev) =>
                    prev.includes(plan.id)
                      ? prev.filter((id) => id !== plan.id)
                      : [...prev, plan.id]
                  )
                }
              />
            ))}
          </Box>
        )}
      </CardContent>
      <ShipmentPlanDialog
        open={isShipmentPlanDialogOpen}
        onClose={handleCloseDialog}
        truckWithVehicle={truckWithVehicle}
        planToEdit={planToEdit}
        createPlanKind={createPlanKind}
        insertRepairAfterLegIndex={insertRepairAfterLegIndex}
      />
      <LegPhotosDialog
        open={!!legPhotosState}
        onClose={() => setLegPhotosState(null)}
        planId={legPhotosState?.planId ?? ''}
        legItems={legPhotosState?.legItems ?? []}
        initialLegIndex={legPhotosState?.initialLegIndex}
        vehicle_damage={legPhotosState?.vehicle_damage ?? undefined}
        onApprovePreDelivery={handleApprovePreDelivery}
        onRejectPreDelivery={handleRejectPreDelivery}
      />
      <EditNeedsDialog
        open={isEditNeedsDialogOpen}
        onClose={() => setIsEditNeedsDialogOpen(false)}
        truckWithVehicle={truckWithVehicle}
        transporterGroups={transporterGroups}
      />
      <TimelineDialog
        open={isTimelineDialogOpen}
        onClose={() => setIsTimelineDialogOpen(false)}
        vehicle={vehicle}
      />
      <ConfirmDialog
        open={!!deleteConfirm}
        title={deleteConfirmTitle}
        message={deleteConfirmMessage}
        confirmText="Delete"
        cancelText="Cancel"
        severity="error"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirm(null)}
      />
      <ConfirmDialog
        open={!!completeConfirm}
        title="Mark leg completed?"
        message="Are you sure you want to mark this leg as completed? This will update the truck's current location and status."
        confirmText="Mark completed"
        cancelText="Cancel"
        severity="warning"
        onConfirm={handleConfirmMarkLegCompleted}
        onCancel={() => setCompleteConfirm(null)}
      />
      <ConfirmDialog
        open={!!flagConfirm}
        title={flagConfirm?.nextFlagged ? 'Flag this truck?' : 'Remove flag from this truck?'}
        message={
          flagConfirm?.nextFlagged
            ? 'This will mark the repair truck for attention.'
            : 'This will clear the attention flag from the repair truck.'
        }
        confirmText={flagConfirm?.nextFlagged ? 'Flag truck' : 'Remove flag'}
        cancelText="Cancel"
        severity="warning"
        onCancel={() => setFlagConfirm(null)}
        onConfirm={async () => {
          if (!flagConfirm) return
          try {
            await updateRepairTruckFlagged({
              repairTruckId: flagConfirm.repairTruckId,
              flagged: flagConfirm.nextFlagged,
            })
          } finally {
            setFlagConfirm(null)
          }
        }}
      />
      <ConfirmDialog
        open={newPlanConfirmOpen}
        title="Create a new shipment plan?"
        message="This truck already has a shipment plan. Are you sure you want to create a new plan instead of editing the existing one?"
        confirmText="Create new plan"
        cancelText="Cancel"
        severity="warning"
        onConfirm={() => {
          setNewPlanConfirmOpen(false)
          handleOpenCreate(pendingCreatePlanKind)
        }}
        onCancel={() => setNewPlanConfirmOpen(false)}
      />
    </Card>
  )
}

export default TransportTruckCard
